import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";

const LOGO_URL = "/dispatch-pay-logo.png";
const LAST_UPDATED = "October 7, 2026";
const CONTACT_EMAIL = "cs@dispatchpaycalc.com";
const COMPANY_LEGAL_NAME = "Ruben Leal (sole proprietor, dba Dispatch Pay Calculator)";
const APP_NAME = "Dispatch Pay Calculator";
const WEBSITE = "www.dispatchpaycalc.com";
const GOVERNING_STATE = "Texas, United States";

export default function Terms() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0B1A3A] border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt={APP_NAME} className="h-10 w-auto" />
          </Link>
          <Link to="/signup">
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="terms-signup-btn">Start free trial</Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16" data-testid="terms-page">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Legal</div>
        <h1 className="mt-2 text-4xl font-semibold text-slate-900">Terms of Service</h1>
        <p className="mt-2 text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-slate mt-10 text-sm leading-relaxed text-slate-700 space-y-5">
          <p>
            Welcome to {APP_NAME} ("the Service"), accessible at <a href={`https://${WEBSITE}`} className="text-blue-700 font-semibold">{WEBSITE}</a> and operated by {COMPANY_LEGAL_NAME} ("we", "us", "our"). By creating a workspace, signing in, or otherwise using the Service, you agree to these Terms of Service ("Terms"). If you do not agree, do not use the Service.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">1. The Service</h2>
          <p>
            {APP_NAME} is a software-as-a-service product for trucking fleets to manage driver records, trip data, pay statements, deductions, and payroll exports. The Service is provided on an "as-is" and "as available" basis.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">2. Your account</h2>
          <p>
            To use the Service you must create a workspace with a company name, administrator username, and password. You are responsible for keeping your credentials secure and for all activity under your account. You must be at least 18 years old and legally able to form a binding contract.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">3. Free trial and subscription</h2>
          <p>
            New workspaces receive a 14-day free trial. After the trial you must subscribe to a paid plan ($49 USD per month, billed monthly through Stripe) to continue using the Service. Subscriptions renew automatically on the same day each month until canceled. You may cancel at any time from the billing portal; cancellation takes effect at the end of the current paid period.
          </p>
          <p>
            All fees are non-refundable except where required by applicable law. We may change pricing with at least 30 days' notice; price changes do not apply to your current paid period.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">4. Acceptable use</h2>
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Use the Service for anything illegal, fraudulent, or that infringes another party's rights.</li>
            <li>Attempt to gain unauthorized access to another workspace, system, or user account.</li>
            <li>Reverse-engineer, decompile, or copy the Service except as allowed by applicable law.</li>
            <li>Interfere with the Service's operation, including by introducing malware or excessive load.</li>
            <li>Use the Service to compute payroll in a way that violates wage and hour laws, tax laws, or labor laws applicable to you.</li>
          </ul>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">5. Your data</h2>
          <p>
            You retain all rights to the data you upload or create in your workspace ("Customer Data"). You grant us a limited, worldwide license to host, process, and display Customer Data solely to provide the Service. We do not sell your Customer Data or use it to train generative AI models.
          </p>
          <p>
            You are responsible for the accuracy of Customer Data, including the correctness of driver pay calculations, tax treatment, deductions, reimbursements, and payroll exports produced by the Service. The Service is a calculation and record-keeping tool, not a tax or legal advisor.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">6. No payroll tax or legal advice</h2>
          <p>
            {APP_NAME} does not withhold, remit, or file payroll taxes on your behalf. You remain solely responsible for complying with federal, state, and local wage, tax, and labor laws. Nothing in the Service constitutes legal, tax, or accounting advice.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">7. Third-party services</h2>
          <p>
            We use third-party services to operate the Service, including Stripe (payments), Emergent (hosting and transactional email), and MongoDB (database). Your use of the Service is also governed by the applicable terms of those providers. We are not responsible for third-party service availability or acts.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">8. Suspension and termination</h2>
          <p>
            We may suspend or terminate your workspace at any time, with or without notice, if you violate these Terms, fail to pay, or if we reasonably believe continued use poses a security, legal, or operational risk. You may terminate your account at any time by canceling your subscription and requesting workspace deletion by email.
          </p>
          <p>
            Upon termination, your workspace will be deactivated and your Customer Data may be deleted after 30 days. Export your data before cancellation if you need to retain it.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">9. Disclaimers</h2>
          <p className="uppercase text-xs tracking-wide">
            The service is provided "as is" and "as available" without warranties of any kind, whether express or implied, including merchantability, fitness for a particular purpose, title, and non-infringement. We do not warrant that the service will be uninterrupted, error-free, or that calculations produced by the service are suitable for any specific regulatory purpose.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">10. Limitation of liability</h2>
          <p className="uppercase text-xs tracking-wide">
            To the maximum extent permitted by law, {COMPANY_LEGAL_NAME.toUpperCase()} shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or for lost profits, revenue, data, or goodwill, arising out of or related to your use of the service. Our total aggregate liability shall not exceed the amounts paid by you to us in the 12 months preceding the claim.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">11. Indemnification</h2>
          <p>
            You will indemnify and hold harmless {COMPANY_LEGAL_NAME}, its affiliates, officers, and employees from any claim, loss, or expense arising out of your Customer Data, your use of the Service, or your violation of these Terms.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">12. Changes to the Terms</h2>
          <p>
            We may update these Terms from time to time. Material changes will be posted on this page with an updated "Last updated" date. Continued use of the Service after changes become effective constitutes acceptance.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">13. Governing law</h2>
          <p>
            These Terms are governed by the laws of {GOVERNING_STATE}, without regard to conflict-of-laws principles. Any dispute will be resolved in the state or federal courts located in {GOVERNING_STATE}, and you consent to personal jurisdiction there.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">14. Contact</h2>
          <p>
            Questions about these Terms? Email <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-700 font-semibold">{CONTACT_EMAIL}</a>.
          </p>
        </div>

        <div className="mt-16 border-t border-slate-200 pt-6 flex items-center justify-between text-xs text-slate-500">
          <Link to="/privacy" className="hover:text-slate-900">Privacy Policy</Link>
          <Link to="/home" className="hover:text-slate-900">← Back to home</Link>
        </div>
      </main>
    </div>
  );
}
